import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { format, addDays, parseISO } from 'date-fns';
import { Rich } from '@agendor/shared';
import {
    Button,
    Input,
    ConfirmDialog,
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogContent,
    DialogFooter,
    Collapse,
    CollapseSummary,
    CollapseBody,
    gtagEvent,
    vars,
    firstName,
    Badge,
} from '@agendor/ui';
import { Modal } from '../components/Modal';
import { FormGroup, InputGroup, TextArea, Checkbox, CheckboxGroup, CheckboxLabel, Select } from '../components/Input';
import { RefreshButton } from '../components/RefreshButton';
import BusinessService, {
    BusinessDetails,
    BusinessEvent,
    BusinessServiceItem,
    Client,
    BookingIntake,
    OverlappingBooking,
    UnavailableTimeError,
    DuplicateBlockedTimeError,
    CheckInFutureOccurrenceError,
    PaymentJustConfirmedError,
    BusinessWorker,
} from '../business-service';
import { useSectionVisibility } from './section-visibility-context';
import { useToast } from '../toast-context';
import { isIOS, isStandalone } from '../platform';
import { formatCurrency } from '../pricing';
import getClientContactInfo from '../get-client-contact-info';
import { buildAgendaItems } from '../agenda-items';
import { WEEKDAYS, effectiveDayIntervals } from '../opening-hours';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 800px;
    margin: 0 auto;
    height: 100%;
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const ControlsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const ModalWrapper = styled.div<{ hideOnDesktop?: boolean }>`
    ${(p) =>
        p.hideOnDesktop &&
        `
        @media (min-width: 601px) {
            display: none;
        }
    `}
`;

const DateNavigationRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const DateNavigation = styled.div`
    display: flex;
    align-items: stretch;
    height: 36px;
    background-color: #fff;
    border-radius: 6px;
    border: 1px solid ${vars.dividerColor};
    position: relative;
    overflow: hidden;
`;

const NavButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${vars.foregroundColor};
    width: 36px;
    height: 100%;
    border-radius: 0;
    display: grid;
    place-items: center;
    font-size: 1rem;
    line-height: 1;

    &:not(:first-child) {
        border-left: 1px solid ${vars.dividerColor};
    }

    &:hover {
        background-color: ${vars.backgroundColorBright};
        color: ${vars.foregroundColor};
    }

    &:active {
        transform: scale(0.95);
    }
`;

const TodayButton = styled(NavButton)`
    border-left: 1px solid ${vars.dividerColor};
`;

const DateDisplay = styled.div`
    font-size: 1rem;
    font-weight: 400;
    color: ${vars.foregroundColor};
    text-align: center;
    text-transform: capitalize;
    user-select: none;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0 12px;
    width: 100%;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    height: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .date-display-container:hover & {
        background-color: ${vars.backgroundColorBright};
    }
`;

const DateDisplayContainer = styled.div`
    position: relative;
    min-width: 180px;
    display: flex;
    justify-content: center;
    align-items: stretch;
    flex: 1;
    min-width: 0;
    border-left: 1px solid ${vars.dividerColor};
    cursor: pointer;

    &:hover > div:last-child {
        background-color: ${vars.backgroundColorBright};
    }
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 200px;
    margin-left: auto;

    @media (max-width: 600px) {
        width: 100%;
        margin-left: 0;
    }
`;

const FilterLabel = styled.span`
    font-size: 0.9rem;
    color: #666;
    font-weight: 600;
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 8px;
    margin-left: auto;
    align-items: center;
`;

const DesktopActions = styled.div`
    display: flex;
    gap: 8px;

    @media (max-width: 600px) {
        display: none;
    }
`;

const ActionsMenuWrapper = styled.div`
    position: relative;

    @media (min-width: 601px) {
        display: none;
    }
`;

const ActionsMenuToggle = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 36px;
    border: 1px solid ${vars.foregroundColor};
    border-radius: 6px;
    background: ${vars.primaryColor};
    cursor: pointer;
    padding: 8px 12px;
    font: inherit;
    color: ${vars.foregroundColor};

    &:hover {
        background-color: ${vars.primaryColorLight};
    }
`;

const ActionsMenuList = styled.div`
    position: absolute;
    right: 0;
    top: 42px;
    background: ${vars.backgroundColor};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    min-width: 180px;
    z-index: 5;
    overflow: hidden;
`;

const ActionsMenuItem = styled.button`
    padding: 10px 12px;
    background: transparent;
    border: none;
    text-align: left;
    cursor: pointer;
    font: inherit;
    color: ${vars.foregroundColor};

    &:hover {
        background-color: ${vars.backgroundColorBright};
    }
`;

// Simple Agenda View Components
const AgendaContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
    padding-bottom: 40px;
`;

const AgendaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HoursMarker = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    font-size: 0.85rem;
    color: #888;

    &::before {
        content: '';
        width: 8px;
        height: 1px;
        background-color: ${vars.dividerColor};
    }

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: ${vars.dividerColor};
    }
`;

const EventCard = styled.div<{ type: string }>`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 8px;

    &:hover {
        border-color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColorBright};
    }

    ${(p) =>
        p.type === 'blocked' &&
        `
        background-color: ${vars.backgroundColorDark};
    `}
`;

const RouteError = styled.div`
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid ${vars.dangerColorDark};
    background: ${vars.dangerTintSoft};
    color: ${vars.foregroundColor};
`;

const EventTitle = styled.div`
    font-weight: 600;
    color: ${vars.foregroundColor};
    font-size: 1.05rem;
`;

const EventMeta = styled.div`
    font-size: 0.9rem;
    color: #666;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 140px;
`;

const MetaLabel = styled.span`
    font-size: 0.8rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
`;

const MetaValue = styled.span`
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const EventDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const DetailRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DetailLabel = styled.span`
    font-size: 0.85rem;
    color: #666;
    font-weight: 500;
`;

const DetailValue = styled.span`
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 500;
`;

const TimeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 0.95rem;
`;

const IntakeCollapseHeader = styled(CollapseSummary)`
    gap: 12px;
`;

const IntakeCollapseTitle = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${vars.foregroundColor};
`;

const IntakeCollapseBody = styled(CollapseBody)`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

type BaseCalendarEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    recurrenceIntervalDays: number | null;
    recurrenceEndDate: string | null;
    worker: { id: number; name: string | null } | null;
    notes: string;
};

type BookingCalendarEvent = BaseCalendarEvent & {
    type: 'booking';
    customerId: number;
    customerName: string | null;
    customerPhoneNumber: string | null;
    customerEmail: string | null;
    services: { id: number; name: string; durationInMinutes: number }[];
    confirmedAt: Date | null;
    checkedInAt: Date | null;
    cancelledAt: Date | null;
    payment: { paidAt: Date | null } | null;
    isLinkedToSubscription: boolean;
    intakeStatus?: 'pending' | 'completed' | null;
};

type BlockedCalendarEvent = BaseCalendarEvent & {
    type: 'blocked';
};

type CalendarEvent = BookingCalendarEvent | BlockedCalendarEvent;

function mapBusinessEventToCalendarEvent(event: BusinessEvent): CalendarEvent {
    const start = toDate(event.start);
    const end = toDate(event.end);
    const recurrenceIntervalDays = event.recurrence.intervalDays;
    const recurrenceEndDate = event.recurrence.endDate;
    const worker = event.worker ? { id: event.worker.id, name: event.worker.name } : null;

    if (event.type === 'booking') {
        const serviceName = formatServices(event.services);
        const title = [event.client.name ?? 'Cliente', serviceName].filter(Boolean).join(' - ') || 'Agendamento';

        return {
            id: event.id,
            title,
            start,
            end,
            type: 'booking',
            worker,
            customerId: event.client.id,
            customerName: event.client.name ?? null,
            customerPhoneNumber: event.client.phoneNumber,
            customerEmail: event.client.email,
            services: event.services,
            recurrenceIntervalDays,
            recurrenceEndDate,
            notes: event.notes,
            confirmedAt: event.confirmedAt ? toDate(event.confirmedAt) : null,
            checkedInAt: event.checkedInAt ? toDate(event.checkedInAt) : null,
            cancelledAt: event.cancelledAt ? toDate(event.cancelledAt) : null,
            payment: event.payment ? { paidAt: event.payment.paidAt ? toDate(event.payment.paidAt) : null } : null,
            isLinkedToSubscription: event.isLinkedToSubscription,
            intakeStatus: event.intakeStatus,
        };
    }

    return {
        id: event.id,
        title: event.title,
        start,
        end,
        type: 'blocked',
        worker,
        recurrenceIntervalDays,
        recurrenceEndDate,
        notes: '',
    };
}

function toDate(plain: {
    date: { year: number; month: number; day: number };
    time: { hour: number; minute: number };
}): Date {
    const { year, month, day } = plain.date;
    const { hour, minute } = plain.time;
    return new Date(year, month - 1, day, hour, minute);
}

function formatServices(services: { name: string }[]): string | undefined {
    const names = services.map((svc) => svc.name).filter(Boolean);
    if (names.length === 0) return undefined;
    if (names.length === 1) return names[0];
    const last = names[names.length - 1];
    const rest = names.slice(0, -1).join(', ');
    return `${rest} e ${last}`;
}

function getServiceName(event: CalendarEvent): string | undefined {
    if (event.type !== 'booking') return undefined;
    return event.services.length > 0 ? formatServices(event.services) : undefined;
}

type DraftBooking = {
    customerName: string;
    customerId: number | null;
    workerId: number | null;
    date: string;
    startTime: string;
    services: number[];
    notes: string;
    recurrenceIntervalDays: number | null;
    recurrenceEndDate: string;
    recurrenceEnabled: boolean;
    isLinkedToSubscription: boolean;
};

type DraftBlock = {
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    scope: 'business' | 'worker';
    workerId: number | null;
    recurrenceIntervalDays: number | null;
    recurrenceEndDate: string;
    recurrenceEnabled: boolean;
};

type Worker = Pick<BusinessWorker, 'id' | 'name' | 'servicesIds' | 'isActive' | 'openingHours'>;

export default function BookingsSection({
    service,
    business,
    workers,
    services: availableServices,
    clients,
    isLoadingClients,
    clientsLoadError,
    ensureClientsLoaded,
    onReloadClients,
    businessLoadError,
    onReloadBusiness,
    isLoadingBusiness,
    modalSegments,
    onNavigate,
    confirmationEnabled,
    checkInEnabled,
    isOwner,
    currentWorkerId,
}: {
    service: BusinessService;
    business: BusinessDetails | null;
    workers: Worker[];
    services: BusinessServiceItem[];
    clients: Client[];
    isLoadingClients: boolean;
    clientsLoadError: string | null;
    ensureClientsLoaded: () => void;
    onReloadClients: () => void;
    businessLoadError: string | null;
    onReloadBusiness: () => void;
    isLoadingBusiness: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    confirmationEnabled: boolean;
    checkInEnabled: boolean;
    isOwner: boolean;
    currentWorkerId: number | null;
}) {
    const isVisible = useSectionVisibility();
    const parseDateFromRoute = useCallback((segments: string[]) => {
        const [maybeDate] = segments;
        if (maybeDate && /^\d{4}-\d{2}-\d{2}$/.test(maybeDate)) {
            const [year, month, day] = maybeDate.split('-').map(Number);
            const parsed = new Date(year, month - 1, day, 12, 0, 0);
            if (!Number.isNaN(parsed.getTime())) return parsed;
        }
        return null;
    }, []);
    const { showToast } = useToast();
    const [selectedDate, setSelectedDate] = useState(() => parseDateFromRoute(modalSegments) ?? new Date());
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [isLoadingEvents, setIsLoadingEvents] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isCreateBlockOpen, setIsCreateBlockOpen] = useState(false);
    const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
    const [modalRouteError, setModalRouteError] = useState<string | null>(null);
    const [draftBooking, setDraftBooking] = useState<DraftBooking>({
        customerName: '',
        customerId: null,
        workerId: null,
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: '10:00',
        services: [],
        notes: '',
        recurrenceIntervalDays: null,
        recurrenceEndDate: '',
        recurrenceEnabled: false,
        isLinkedToSubscription: false,
    });
    const [draftBlock, setDraftBlock] = useState<DraftBlock>({
        title: '',
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: '12:00',
        endTime: '13:00',
        scope: 'business',
        workerId: null,
        recurrenceIntervalDays: null,
        recurrenceEndDate: '',
        recurrenceEnabled: false,
    });
    const [workerFilter, setWorkerFilter] = useState<number | 'all'>('all');
    const [showCancelled, setShowCancelled] = useState(false);
    // Keys to force re-mount of time inputs after rounding on blur.
    // This fixes browser's internal state not syncing with React state.
    const [bookingTimeKey, setBookingTimeKey] = useState(0);
    const [blockTimeKey, setBlockTimeKey] = useState(0);

    // Booking creation state
    const [isCreating, setIsCreating] = useState(false);
    const [isCheckingIn, setIsCheckingIn] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: React.ReactNode;
        confirmText: string;
        isRecurring: boolean;
        scope: 'single' | 'all' | null;
        onConfirm: (scope?: 'single' | 'all') => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        confirmText: '',
        isRecurring: false,
        scope: null,
        onConfirm: () => {},
    });

    const [blockOverlapDialog, setBlockOverlapDialog] = useState<{
        isOpen: boolean;
        bookings: OverlappingBooking[];
        rangeCappedAt: { year: number; month: number; day: number } | null;
        confirmationId: string | null;
    }>({ isOpen: false, bookings: [], rangeCappedAt: null, confirmationId: null });

    const [failedCancellationsDialog, setFailedCancellationsDialog] = useState<{
        isOpen: boolean;
        bookings: OverlappingBooking[];
        isBusinessScope: boolean;
    }>({ isOpen: false, bookings: [], isBusinessScope: false });

    type BookingIntakeState =
        | { status: 'idle' }
        | { status: 'loading' }
        | { status: 'loaded'; intakes: BookingIntake[] }
        | { status: 'error' };
    const [bookingIntakeState, setBookingIntakeState] = useState<BookingIntakeState>({ status: 'idle' });
    const bookingIntakeCacheRef = useRef<Map<string, BookingIntake[]>>(new Map());

    const selectedClient = clients.find((c) => c.id === draftBooking.customerId) ?? null;

    // Refresh button (iOS PWA only)
    const showRefreshButton = useMemo(() => isIOS() && isStandalone(), []);

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('pt-BR', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
        }).format(date);
    };

    const formatTime = (date: Date) => {
        return new Intl.DateTimeFormat('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const loadRequestIdRef = useRef(0);
    const hasLoadedOnceRef = useRef(false);
    const lastLoadedDateRef = useRef<Date | null>(null);
    const [pendingDetailId, setPendingDetailId] = useState<string | null>(null);

    const loadEventsForDate = useCallback(
        async (targetDate: Date) => {
            const requestId = ++loadRequestIdRef.current;
            lastLoadedDateRef.current = targetDate;
            bookingIntakeCacheRef.current.clear();
            setIsLoadingEvents(true);
            setLoadError(null);
            try {
                const data = await service.getEvents({ date: targetDate });
                if (requestId !== loadRequestIdRef.current) return;
                setEvents(data.map(mapBusinessEventToCalendarEvent));
            } catch (error) {
                if (requestId !== loadRequestIdRef.current) return;
                console.error('Failed to fetch events', error);
                setEvents([]);
                setLoadError('Não foi possível carregar os agendamentos.');
            } finally {
                if (requestId === loadRequestIdRef.current) {
                    setIsLoadingEvents(false);
                }
            }
        },
        [service],
    );

    const handleRefresh = useCallback(() => {
        gtagEvent('bookings_refresh_click');
        loadEventsForDate(selectedDate);
    }, [loadEventsForDate, selectedDate]);

    useEffect(
        () => () => {
            loadRequestIdRef.current += 1;
        },
        [],
    );

    useEffect(() => {
        loadRequestIdRef.current += 1;
        hasLoadedOnceRef.current = false;
        lastLoadedDateRef.current = null;
        setEvents([]);
        setLoadError(null);
        setIsLoadingEvents(true);
    }, [service]);

    useEffect(() => {
        if (!isVisible || hasLoadedOnceRef.current) return;
        hasLoadedOnceRef.current = true;
        loadEventsForDate(selectedDate);
    }, [isVisible, loadEventsForDate, selectedDate]);

    const modalSegmentsWithoutDate = React.useMemo(() => {
        const [first, ...rest] = modalSegments;
        return /^\d{4}-\d{2}-\d{2}$/.test(first ?? '') ? rest : modalSegments;
    }, [modalSegments]);

    const routeDate = React.useMemo(() => parseDateFromRoute(modalSegments), [modalSegments, parseDateFromRoute]);

    useEffect(() => {
        if (!isVisible) return;
        setModalRouteError(null);
        if (routeDate && routeDate.toDateString() !== selectedDate.toDateString()) {
            setSelectedDate(routeDate);
            setShowCancelled(false);
            return;
        }
        if (!routeDate) {
            const dateString = format(selectedDate, 'yyyy-MM-dd');
            onNavigate([dateString, ...modalSegmentsWithoutDate], true);
            return;
        }
        if (modalSegmentsWithoutDate.length === 0) {
            setPendingDetailId(null);
            setSelectedEvent(null);
            setIsCreateModalOpen(false);
            setIsCreateBlockOpen(false);
            return;
        }
        const [first, second] = modalSegmentsWithoutDate;
        if (first === 'new') {
            setIsCreateModalOpen(true);
            setIsCreateBlockOpen(false);
            setPendingDetailId(null);
            setSelectedEvent(null);
            return;
        }
        if (first === 'blocks' && second === 'new') {
            setIsCreateBlockOpen(true);
            setIsCreateModalOpen(false);
            setPendingDetailId(null);
            setSelectedEvent(null);
            return;
        }
        if (modalSegmentsWithoutDate.length > 1) {
            setModalRouteError('Caminho de modal inválido para agendamentos.');
            const dateString = format(routeDate ?? selectedDate, 'yyyy-MM-dd');
            onNavigate([dateString], true);
            return;
        }
        setPendingDetailId(first);
        setSelectedEvent(null);
        setIsCreateModalOpen(false);
        setIsCreateBlockOpen(false);
    }, [modalSegmentsWithoutDate, routeDate, selectedDate, onNavigate, isVisible]);

    useEffect(() => {
        if (!pendingDetailId) return;
        const event = events.find((e) => e.id === pendingDetailId) ?? null;
        if (event) {
            setSelectedEvent(event);
            return;
        }
        if (isLoadingEvents) return;
        showToast('Agendamento não encontrado.', 'error', 'app');
        onNavigate([format(selectedDate, 'yyyy-MM-dd')], true);
        setPendingDetailId(null);
        setSelectedEvent(null);
    }, [events, pendingDetailId, isLoadingEvents, onNavigate, selectedDate, showToast]);

    useEffect(() => {
        if (!isVisible || !hasLoadedOnceRef.current) return;
        if (lastLoadedDateRef.current?.getTime() === selectedDate.getTime()) return;
        loadEventsForDate(selectedDate);
    }, [isVisible, selectedDate, loadEventsForDate]);

    const loadBookingIntakes = useCallback(async () => {
        if (!selectedEvent || selectedEvent.type !== 'booking' || selectedEvent.intakeStatus !== 'completed') {
            setBookingIntakeState({ status: 'idle' });
            return;
        }
        const cached = bookingIntakeCacheRef.current.get(selectedEvent.id);
        if (cached) {
            setBookingIntakeState({ status: 'loaded', intakes: cached });
            return;
        }
        setBookingIntakeState({ status: 'loading' });
        try {
            const intakes = await service.getBookingIntake(selectedEvent.id);
            bookingIntakeCacheRef.current.set(selectedEvent.id, intakes);
            setBookingIntakeState({ status: 'loaded', intakes });
        } catch {
            setBookingIntakeState({ status: 'error' });
        }
    }, [selectedEvent, service]);

    useEffect(() => {
        loadBookingIntakes();
    }, [loadBookingIntakes]);

    // Ensure clients are loaded when create modal opens
    useEffect(() => {
        if (!isCreateModalOpen) return;
        ensureClientsLoaded();
    }, [isCreateModalOpen, ensureClientsLoaded]);

    const handleRetryLoadEvents = () => {
        loadEventsForDate(selectedDate);
    };

    const handlePrevDay = () => {
        gtagEvent('bookings_date_nav_click', { action: 'prev' });
        setIsLoadingEvents(true);
        setShowCancelled(false);
        const prev = new Date(selectedDate);
        prev.setDate(prev.getDate() - 1);
        setSelectedDate(prev);
        onNavigate([format(prev, 'yyyy-MM-dd'), ...modalSegmentsWithoutDate], true);
    };

    const handleNextDay = () => {
        gtagEvent('bookings_date_nav_click', { action: 'next' });
        setIsLoadingEvents(true);
        setShowCancelled(false);
        const next = new Date(selectedDate);
        next.setDate(next.getDate() + 1);
        setSelectedDate(next);
        onNavigate([format(next, 'yyyy-MM-dd'), ...modalSegmentsWithoutDate], true);
    };

    const handleToday = () => {
        gtagEvent('bookings_date_nav_click', { action: 'today' });
        setIsLoadingEvents(true);
        setShowCancelled(false);
        const today = new Date();
        setSelectedDate(today);
        onNavigate([format(today, 'yyyy-MM-dd'), ...modalSegmentsWithoutDate], true);
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    const handleSelectEvent = (event: CalendarEvent) => {
        gtagEvent('bookings_event_click', { type: event.type });
        onNavigate([format(selectedDate, 'yyyy-MM-dd'), event.id]);
    };

    const handleCloseModal = () => {
        setPendingDetailId(null);
        setSelectedEvent(null);
        onNavigate([format(selectedDate, 'yyyy-MM-dd')]);
    };

    const handleOpenCreateModal = useCallback(() => {
        gtagEvent('bookings_new_click');
        const activeWorkers = workers.filter((w) => w.isActive);
        const defaultWorkerId = currentWorkerId ?? (activeWorkers.length === 1 ? activeWorkers[0].id : null);
        setDraftBooking({
            customerName: '',
            customerId: null,
            workerId: defaultWorkerId,
            date: format(selectedDate, 'yyyy-MM-dd'),
            startTime: '10:00',
            services: [],
            notes: '',
            recurrenceIntervalDays: null,
            recurrenceEndDate: '',
            recurrenceEnabled: false,
            isLinkedToSubscription: false,
        });
        onNavigate([format(selectedDate, 'yyyy-MM-dd'), 'new']);
    }, [onNavigate, selectedDate, workers, currentWorkerId]);

    const handleCloseCreateModal = () => {
        gtagEvent('bookings_create_cancel_click');
        onNavigate([format(selectedDate, 'yyyy-MM-dd')]);
    };

    const handleDraftChange = (field: keyof DraftBooking, value: any) => {
        setDraftBooking((prev) => ({ ...prev, [field]: value }));
    };

    const handleToggleService = (serviceId: number) => {
        setDraftBooking((prev) => {
            const has = prev.services.includes(serviceId);
            const services = has ? prev.services.filter((id) => id !== serviceId) : [...prev.services, serviceId];
            return { ...prev, services };
        });
    };

    const handleOpenCreateBlock = () => {
        gtagEvent('bookings_block_new_click');
        setDraftBlock({
            title: '',
            date: format(selectedDate, 'yyyy-MM-dd'),
            startTime: '12:00',
            endTime: '13:00',
            scope: currentWorkerId ? 'worker' : 'business',
            workerId: currentWorkerId,
            recurrenceIntervalDays: null,
            recurrenceEndDate: '',
            recurrenceEnabled: false,
        });
        setIsActionsMenuOpen(false);
        onNavigate([format(selectedDate, 'yyyy-MM-dd'), 'blocks', 'new']);
    };

    const handleCloseCreateBlock = () => {
        gtagEvent('bookings_block_cancel_click');
        onNavigate([format(selectedDate, 'yyyy-MM-dd')]);
    };

    const handleDraftBlockChange = (field: keyof DraftBlock, value: any) => {
        setDraftBlock((prev) => ({ ...prev, [field]: value }));
    };

    const handleCreateBlock = async () => {
        gtagEvent('bookings_block_save_click');
        const { title, startTime, endTime, scope, workerId } = draftBlock;

        if (!title) {
            showToast('Título é obrigatório.', 'error', 'modal');
            return;
        }
        if (!startTime) {
            showToast('Horário de início é obrigatório.', 'error', 'modal');
            return;
        }
        if (!endTime) {
            showToast('Horário de término é obrigatório.', 'error', 'modal');
            return;
        }

        if (scope === 'worker' && !workerId) {
            showToast('Profissional é obrigatório.', 'error', 'modal');
            return;
        }

        const [startHours, startMinutes] = startTime.split(':').map(Number);
        if (Number.isNaN(startHours) || Number.isNaN(startMinutes)) {
            showToast('Horário de início inválido.', 'error', 'modal');
            return;
        }

        const [endHours, endMinutes] = endTime.split(':').map(Number);
        if (Number.isNaN(endHours) || Number.isNaN(endMinutes)) {
            showToast('Horário de término inválido.', 'error', 'modal');
            return;
        }

        const startTotal = startHours * 60 + startMinutes;
        const endTotal = endHours * 60 + endMinutes;
        if (endTotal <= startTotal) {
            showToast('Horário de término deve ser após o início.', 'error', 'modal');
            return;
        }

        await submitBlockedTime({ ...buildBlockParams(draftBlock), confirmationId: null });
    };

    const submitBlockedTime = async (params: {
        title: string;
        date: { year: number; month: number; day: number };
        start: { hour: number; minute: number };
        end: { hour: number; minute: number };
        workerId: number | null;
        recurrence: { intervalDays: number; endDate: { year: number; month: number; day: number } | null } | null;
        confirmationId: string | null;
    }) => {
        try {
            setIsCreating(true);
            const result = await service.createBlockedTime(params);
            if (result.outcome === 'overlaps_bookings') {
                setBlockOverlapDialog({
                    isOpen: true,
                    bookings: result.bookings,
                    rangeCappedAt: result.rangeCappedAt,
                    confirmationId: result.confirmationId,
                });
                return;
            }
            if (result.failedCancellations.length > 0) {
                setFailedCancellationsDialog({
                    isOpen: true,
                    bookings: result.failedCancellations,
                    isBusinessScope: params.workerId === null,
                });
            } else {
                showToast('Bloqueio criado com sucesso!', 'success', 'app');
            }
            handleCloseCreateBlock();
            loadEventsForDate(selectedDate);
        } catch (error) {
            console.error(error);
            if (error instanceof UnavailableTimeError) {
                showToast('Horário não está disponível.', 'error', 'modal');
            } else if (error instanceof DuplicateBlockedTimeError) {
                showToast('Já existe um bloqueio igual a este.', 'error', 'modal');
            } else {
                showToast('Não foi possível criar o bloqueio.', 'error', 'modal');
            }
        } finally {
            setIsCreating(false);
        }
    };

    const handleConfirmBlockOverlap = async () => {
        setBlockOverlapDialog((prev) => ({ ...prev, isOpen: false }));
        await submitBlockedTime({
            ...buildBlockParams(draftBlock),
            confirmationId: blockOverlapDialog.confirmationId,
        });
    };

    const totalDurationMinutes = draftBooking.services
        .map((id) => availableServices.find((s) => s.id === id)?.durationInMinutes || 0)
        .reduce((sum, minutes) => sum + minutes, 0);

    const roundTimeToInterval = (time: string, intervalMinutes: number): string => {
        const [hours, minutes] = time.split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
        const roundedMinutes = Math.round(minutes / intervalMinutes) * intervalMinutes;
        const adjustedHours = hours + Math.floor(roundedMinutes / 60);
        const finalMinutes = roundedMinutes % 60;
        return `${String(adjustedHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
    };

    // HTML <input type="time"> only accepts 00:00-23:59, so '24:00' (end-of-day) is stored
    // as the state sentinel and rendered as '23:59' in the input. Any input value of '23:59'
    // is canonicalized back to '24:00' so submissions to the backend represent end-of-day exactly.
    const canonicalizeEndTime = (value: string): string => (value === '23:59' ? '24:00' : value);
    const displayEndTime = (value: string): string => (value === '24:00' ? '23:59' : value);

    const computedEndTime = (() => {
        const [startHours, startMinutes] = draftBooking.startTime.split(':').map(Number);
        if (Number.isNaN(startHours) || Number.isNaN(startMinutes)) return null;
        const startDate = new Date(selectedDate);
        startDate.setHours(startHours, startMinutes, 0, 0);
        if (Number.isNaN(startDate.getTime())) return null;
        const endDate = new Date(startDate.getTime() + totalDurationMinutes * 60000);
        return format(endDate, 'HH:mm');
    })();

    const handleCreateBooking = async () => {
        gtagEvent('bookings_create_save_click');
        const {
            workerId,
            services: selectedServiceIds,
            notes,
            recurrenceEnabled,
            recurrenceIntervalDays,
            startTime,
        } = draftBooking;
        if (!workerId) {
            showToast('Profissional é obrigatório.', 'error', 'modal');
            return;
        }
        if (selectedServiceIds.length === 0) {
            showToast('Serviço é obrigatório.', 'error', 'modal');
            return;
        }
        if (!draftBooking.customerId) {
            showToast('Cliente é obrigatório.', 'error', 'modal');
            return;
        }
        if (!draftBooking.date) {
            showToast('Data é obrigatória.', 'error', 'modal');
            return;
        }
        if (!startTime) {
            showToast('Horário é obrigatório.', 'error', 'modal');
            return;
        }

        const [startHours, startMinutes] = startTime.split(':').map(Number);
        if (Number.isNaN(startHours) || Number.isNaN(startMinutes)) {
            showToast('Horário inválido.', 'error', 'modal');
            return;
        }

        const [year, month, day] = draftBooking.date.split('-').map(Number);

        if (totalDurationMinutes <= 0) {
            showToast('Duração deve ser maior que zero.', 'error', 'modal');
            return;
        }

        // Optional: Validate end time is after start time (implicit with duration > 0)
        // But if end crosses midnight or something complex, we haven't implemented that logic fully yet.
        // For now, simple duration validation is enough.

        try {
            setIsCreating(true);

            const bookingData = {
                clientId: draftBooking.customerId!,
                workerId: workerId!,
                servicesIds: selectedServiceIds,
                date: { year, month, day },
                time: { hour: startHours, minute: startMinutes },
                notes,
                recurrence:
                    recurrenceEnabled && recurrenceIntervalDays !== null
                        ? {
                              intervalDays: recurrenceIntervalDays,
                              endDate: draftBooking.recurrenceEndDate
                                  ? (() => {
                                        const [y, m, d] = draftBooking.recurrenceEndDate.split('-').map(Number);
                                        return { year: y, month: m, day: d };
                                    })()
                                  : null,
                          }
                        : null,
                isLinkedToSubscription: draftBooking.isLinkedToSubscription,
            };

            await service.createBooking(bookingData);
            showToast('Agendamento criado com sucesso!', 'success', 'app');
            handleCloseCreateModal();
            loadEventsForDate(selectedDate);
        } catch (error) {
            console.error(error);
            if (error instanceof UnavailableTimeError) {
                showToast('Horário não está disponível.', 'error', 'modal');
            } else {
                showToast('Não foi possível criar o agendamento.', 'error', 'modal');
            }
        } finally {
            setIsCreating(false);
        }
    };

    const handleCheckIn = async () => {
        if (!selectedEvent || selectedEvent.type !== 'booking') return;
        gtagEvent('bookings_checkin_click');

        setIsCheckingIn(true);
        try {
            const { year, month, day } = {
                year: selectedEvent.start.getFullYear(),
                month: selectedEvent.start.getMonth() + 1,
                day: selectedEvent.start.getDate(),
            };
            await service.checkInBooking({
                bookingId: selectedEvent.id,
                occurrenceDate: { year, month, day },
            });
            showToast('Check-in realizado com sucesso!', 'success', 'app');
            handleCloseModal();
            loadEventsForDate(selectedDate);
        } catch (error) {
            console.error(error);
            if (error instanceof CheckInFutureOccurrenceError) {
                showToast('Check-in só é permitido para agendamentos de hoje ou passados.', 'error', 'modal');
            } else {
                showToast('Não foi possível realizar o check-in.', 'error', 'modal');
            }
        } finally {
            setIsCheckingIn(false);
        }
    };

    const renderModalContent = () => {
        if (!selectedEvent) return null;

        const servicesToShow: { id: number; name: string; durationInMinutes: number }[] =
            selectedEvent.type === 'booking' ? selectedEvent.services : [];

        return (
            <EventDetails>
                <DetailRow>
                    <DetailLabel>Horário</DetailLabel>
                    <DetailValue>
                        {formatTime(selectedEvent.start)} - {formatTime(selectedEvent.end)}
                    </DetailValue>
                </DetailRow>

                {selectedEvent.type === 'booking' && (
                    <>
                        <DetailRow>
                            <DetailLabel>Profissional</DetailLabel>
                            <DetailValue>{selectedEvent.worker?.name}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                            <DetailLabel>Serviços</DetailLabel>
                            <DetailValue>
                                {servicesToShow.length > 0
                                    ? servicesToShow.map((svc) => <div key={svc.id}>{svc.name}</div>)
                                    : 'Serviço'}
                            </DetailValue>
                        </DetailRow>
                        <DetailRow>
                            <DetailLabel>Cliente</DetailLabel>
                            <DetailValue>{selectedEvent.customerName}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                            <DetailLabel>
                                {
                                    getClientContactInfo({
                                        phoneNumber: selectedEvent.customerPhoneNumber,
                                        email: selectedEvent.customerEmail,
                                    }).label
                                }
                            </DetailLabel>
                            <DetailValue>
                                {
                                    getClientContactInfo({
                                        phoneNumber: selectedEvent.customerPhoneNumber,
                                        email: selectedEvent.customerEmail,
                                    }).value
                                }
                            </DetailValue>
                        </DetailRow>
                        {confirmationEnabled && (
                            <DetailRow>
                                <DetailLabel>Confirmação</DetailLabel>
                                <DetailValue>
                                    {selectedEvent.confirmedAt
                                        ? `Confirmado em ${format(selectedEvent.confirmedAt, 'dd/MM/yyyy')} às ${format(selectedEvent.confirmedAt, 'HH:mm')}`
                                        : selectedEvent.start < new Date() || selectedEvent.cancelledAt
                                          ? 'Não confirmado'
                                          : 'Aguardando confirmação'}
                                </DetailValue>
                            </DetailRow>
                        )}
                        {selectedEvent.payment && (selectedEvent.payment.paidAt || !selectedEvent.cancelledAt) && (
                            <DetailRow>
                                <DetailLabel>Pagamento</DetailLabel>
                                <DetailValue>
                                    {selectedEvent.payment.paidAt
                                        ? `Pago em ${format(selectedEvent.payment.paidAt, 'dd/MM/yyyy')} às ${format(selectedEvent.payment.paidAt, 'HH:mm')}`
                                        : 'Aguardando pagamento'}
                                </DetailValue>
                            </DetailRow>
                        )}
                        {checkInEnabled && (
                            <DetailRow>
                                <DetailLabel>Check-in</DetailLabel>
                                <DetailValue>
                                    {selectedEvent.checkedInAt
                                        ? `Check-in em ${format(selectedEvent.checkedInAt, 'dd/MM/yyyy')} às ${format(selectedEvent.checkedInAt, 'HH:mm')}`
                                        : 'Não realizado'}
                                </DetailValue>
                            </DetailRow>
                        )}
                        {selectedEvent.cancelledAt && (
                            <DetailRow>
                                <DetailLabel>Cancelamento</DetailLabel>
                                <DetailValue>
                                    Cancelado em {format(selectedEvent.cancelledAt, 'dd/MM/yyyy')} às{' '}
                                    {format(selectedEvent.cancelledAt, 'HH:mm')}
                                </DetailValue>
                            </DetailRow>
                        )}
                        {selectedEvent.isLinkedToSubscription && (
                            <DetailRow>
                                <DetailLabel>Vínculo</DetailLabel>
                                <DetailValue>Assinatura</DetailValue>
                            </DetailRow>
                        )}
                        {selectedEvent.intakeStatus === 'pending' ? (
                            <DetailRow>
                                <DetailLabel>Formulários</DetailLabel>
                                <DetailValue>Aguardando resposta do cliente.</DetailValue>
                            </DetailRow>
                        ) : selectedEvent.intakeStatus === 'completed' ? (
                            <Collapse data-testid="booking-intakes-collapse">
                                <IntakeCollapseHeader>
                                    <IntakeCollapseTitle>Formulários</IntakeCollapseTitle>
                                </IntakeCollapseHeader>
                                <IntakeCollapseBody>
                                    {bookingIntakeState.status === 'loading' ? (
                                        <div style={{ textAlign: 'center', padding: '8px', color: '#888' }}>
                                            Carregando...
                                        </div>
                                    ) : bookingIntakeState.status === 'error' ? (
                                        <div style={{ textAlign: 'center', padding: '8px', color: '#888' }}>
                                            Não foi possível carregar os formulários.
                                            <br />
                                            <Button onClick={loadBookingIntakes} style={{ marginTop: '12px' }}>
                                                Tentar novamente
                                            </Button>
                                        </div>
                                    ) : bookingIntakeState.status === 'loaded' ? (
                                        bookingIntakeState.intakes.map((intake, idx) => (
                                            <div
                                                key={intake.id}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '12px',
                                                    paddingTop: idx === 0 ? 0 : '16px',
                                                    borderTop: idx === 0 ? 'none' : `1px solid ${vars.dividerColor}`,
                                                }}
                                                data-testid={`booking-intake-section-${intake.id}`}
                                            >
                                                <IntakeCollapseTitle>{intake.name}</IntakeCollapseTitle>
                                                {intake.questions.map((question) => {
                                                    const answer = intake.answers.find(
                                                        (a) => a.questionId === question.id,
                                                    );
                                                    if (!answer) return null;
                                                    const displayAnswer =
                                                        question.type === 'boolean'
                                                            ? answer.answerText === 'true'
                                                                ? 'Sim'
                                                                : 'Não'
                                                            : answer.answerText;
                                                    return (
                                                        <DetailRow key={question.id}>
                                                            <DetailLabel>{question.questionText}</DetailLabel>
                                                            <DetailValue>{displayAnswer}</DetailValue>
                                                        </DetailRow>
                                                    );
                                                })}
                                            </div>
                                        ))
                                    ) : null}
                                </IntakeCollapseBody>
                            </Collapse>
                        ) : null}
                        {checkInEnabled &&
                            !selectedEvent.checkedInAt &&
                            !selectedEvent.cancelledAt &&
                            !(selectedEvent.payment && !selectedEvent.payment.paidAt) && (
                                <div
                                    style={{
                                        borderTop: `1px solid ${vars.dividerColor}`,
                                        paddingTop: '20px',
                                        marginTop: '8px',
                                    }}
                                >
                                    <Button
                                        onClick={handleCheckIn}
                                        disabled={isCreating || isCheckingIn}
                                        data-testid="checkin-btn"
                                    >
                                        {isCheckingIn ? 'Fazendo check-in...' : 'Fazer Check-in'}
                                    </Button>
                                </div>
                            )}
                    </>
                )}
                {selectedEvent.type === 'blocked' && (
                    <>
                        <DetailRow>
                            <DetailLabel>Título</DetailLabel>
                            <DetailValue>{selectedEvent.title}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                            <DetailLabel>Escopo</DetailLabel>
                            <DetailValue>{selectedEvent.worker?.name ? 'Profissional' : 'Estabelecimento'}</DetailValue>
                        </DetailRow>
                        {selectedEvent.worker?.name && (
                            <DetailRow>
                                <DetailLabel>Profissional</DetailLabel>
                                <DetailValue>{selectedEvent.worker.name}</DetailValue>
                            </DetailRow>
                        )}
                    </>
                )}

                {selectedEvent.notes && (
                    <DetailRow>
                        <DetailLabel>Observações</DetailLabel>
                        <DetailValue>{selectedEvent.notes}</DetailValue>
                    </DetailRow>
                )}

                {selectedEvent.recurrenceIntervalDays !== null && (
                    <DetailRow>
                        <DetailLabel>Recorrência</DetailLabel>
                        <DetailValue>
                            A cada {selectedEvent.recurrenceIntervalDays}{' '}
                            {selectedEvent.recurrenceIntervalDays === 1 ? 'dia' : 'dias'}
                            {selectedEvent.recurrenceEndDate &&
                                (() => {
                                    const [y, m, d] = selectedEvent.recurrenceEndDate.split('-').map(Number);
                                    return ` até ${format(new Date(y, m - 1, d), 'dd/MM/yyyy')}`;
                                })()}
                        </DetailValue>
                    </DetailRow>
                )}
            </EventDetails>
        );
    };

    const closeConfirmDialog = () => {
        setConfirmDialog({
            isOpen: false,
            title: '',
            message: '',
            confirmText: '',
            isRecurring: false,
            scope: null,
            onConfirm: () => {},
        });
    };

    const handleCancelBookingClick = () => {
        if (!selectedEvent || selectedEvent.type !== 'booking') return;
        gtagEvent('bookings_cancel_click', { type: 'booking' });

        const eventToCancel = selectedEvent;
        const isRecurring = eventToCancel.recurrenceIntervalDays !== null;

        const hasPaidDeposit = eventToCancel.payment?.paidAt !== null && eventToCancel.payment?.paidAt !== undefined;

        setConfirmDialog({
            isOpen: true,
            title: 'Cancelar Agendamento',
            message: (
                <>
                    {isRecurring
                        ? 'Este é um agendamento recorrente. Deseja cancelar apenas este ou este e todos os seguintes?'
                        : 'Tem certeza que deseja cancelar este agendamento?'}
                    {hasPaidDeposit && (
                        <p style={{ marginTop: 8, fontWeight: 500 }}>O sinal pago pelo cliente será estornado.</p>
                    )}
                </>
            ),
            confirmText: 'Cancelar',
            isRecurring,
            scope: isRecurring ? null : 'all',
            onConfirm: async (scope) => {
                try {
                    setIsCreating(true);
                    await service.deleteBooking({
                        bookingId: eventToCancel.id,
                        referenceDate: eventToCancel.start,
                        scope,
                    });
                    showToast('Agendamento cancelado com sucesso!', 'success', 'app');
                    closeConfirmDialog();
                    handleCloseModal();
                    loadEventsForDate(selectedDate);
                } catch (error) {
                    if (error instanceof PaymentJustConfirmedError) {
                        showToast('O pagamento deste agendamento acabou de ser confirmado.', 'error', 'app');
                        handleCloseModal();
                        loadEventsForDate(selectedDate);
                    } else {
                        console.error(error);
                        showToast('Não foi possível cancelar o agendamento.', 'error', 'app');
                    }
                } finally {
                    setIsCreating(false);
                }
            },
        });
    };

    const handleDeleteBlockedTimeClick = () => {
        if (!selectedEvent || selectedEvent.type !== 'blocked') return;
        gtagEvent('bookings_delete_click', { type: 'blocked' });

        const eventToDelete = selectedEvent;
        const isRecurring = eventToDelete.recurrenceIntervalDays !== null;

        setConfirmDialog({
            isOpen: true,
            title: 'Remover Bloqueio',
            message: isRecurring
                ? 'Este é um bloqueio recorrente. Deseja remover apenas este ou este e todos os seguintes?'
                : 'Tem certeza que deseja remover este bloqueio? Esta ação não pode ser desfeita.',
            confirmText: 'Remover',
            isRecurring,
            scope: isRecurring ? null : 'all',
            onConfirm: async (scope) => {
                try {
                    setIsCreating(true);
                    await service.deleteBlockedTime({
                        eventId: eventToDelete.id,
                        referenceDate: eventToDelete.start,
                        scope,
                    });
                    showToast('Bloqueio removido com sucesso!', 'success', 'app');
                    closeConfirmDialog();
                    handleCloseModal();
                    loadEventsForDate(selectedDate);
                } catch (error) {
                    console.error(error);
                    showToast('Não foi possível remover o bloqueio.', 'error', 'app');
                } finally {
                    setIsCreating(false);
                }
            },
        });
    };

    const renderModalFooter = () => {
        if (!selectedEvent) return null;

        if (selectedEvent.type === 'booking') {
            const isCancelled = selectedEvent.cancelledAt !== null;
            return (
                <>
                    <Button data-testid="modal-close" onClick={handleCloseModal} disabled={isCreating || isCheckingIn}>
                        Fechar
                    </Button>
                    {!isCancelled && (
                        <Button
                            data-testid="modal-cancel"
                            danger
                            onClick={handleCancelBookingClick}
                            disabled={isCreating || isCheckingIn}
                        >
                            Cancelar
                        </Button>
                    )}
                </>
            );
        }

        return (
            <>
                <Button data-testid="modal-close" onClick={handleCloseModal} disabled={isCreating}>
                    Fechar
                </Button>
                <Button data-testid="modal-delete" danger onClick={handleDeleteBlockedTimeClick} disabled={isCreating}>
                    Remover
                </Button>
            </>
        );
    };

    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const [year, month, day] = event.target.value.split('-').map(Number);
        setIsLoadingEvents(true);
        setShowCancelled(false);
        const nextDate = new Date(year, month - 1, day, 12, 0, 0);
        setSelectedDate(nextDate);
        onNavigate([format(nextDate, 'yyyy-MM-dd'), ...modalSegmentsWithoutDate], true);
    };

    const isLoading = isLoadingEvents;

    // Filter events for selected day
    const eventsForDate = events.filter(
        (event) =>
            event.start.getDate() === selectedDate.getDate() &&
            event.start.getMonth() === selectedDate.getMonth() &&
            event.start.getFullYear() === selectedDate.getFullYear(),
    );

    // Count cancelled bookings (before other filters)
    const cancelledCount = eventsForDate.filter(
        (event) => event.type === 'booking' && event.cancelledAt !== null,
    ).length;

    const daysEvents = eventsForDate.filter((event) => {
        // Worker filter
        if (workerFilter !== 'all') {
            if (event.type === 'booking' && event.worker?.id !== workerFilter) return false;
            if (event.type === 'blocked' && event.worker !== null && event.worker.id !== workerFilter) return false;
        }
        // Cancelled filter
        if (!showCancelled && event.type === 'booking' && event.cancelledAt !== null) {
            return false;
        }
        return true;
    });

    // Sort events by start time
    daysEvents.sort((a, b) => a.start.getTime() - b.start.getTime());

    // The agenda is narrowed to one professional by the filter, or by being logged in as one.
    const agendaWorkerId = workerFilter === 'all' ? currentWorkerId : workerFilter;
    const agendaWorker = agendaWorkerId === null ? null : (workers.find((w) => w.id === agendaWorkerId) ?? null);

    // Generate agenda items: events + hours markers + empty interval placeholders
    const agendaItems = useMemo(() => {
        const dayOfWeek = WEEKDAYS[selectedDate.getDay()];
        const businessIntervals = business?.openingHours[dayOfWeek] ?? [];
        const intervals = effectiveDayIntervals({
            weekday: dayOfWeek,
            businessIntervals,
            workerOpeningHours: agendaWorker?.openingHours ?? null,
        });

        const eventInputs = daysEvents.map((e) => ({ startMinutes: e.start.getHours() * 60 + e.start.getMinutes() }));
        const abstractItems = buildAgendaItems(intervals, eventInputs);

        // Map abstract items back to concrete items (events need full data)
        let eventIndex = 0;
        return abstractItems.map((item) => {
            if (item.type === 'event') {
                return daysEvents[eventIndex++];
            }
            return item;
        });
    }, [business, selectedDate, daysEvents, agendaWorker]);

    return (
        <Container>
            <Header>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <Title>Agendamentos</Title>
                    {modalRouteError && <RouteError>{modalRouteError}</RouteError>}
                    <HeaderActions>
                        <DesktopActions>
                            <Button data-testid="new-block-btn" onClick={handleOpenCreateBlock}>
                                Novo Bloqueio
                            </Button>
                            <Button data-testid="new-booking-btn" primary onClick={handleOpenCreateModal}>
                                Novo Agendamento
                            </Button>
                        </DesktopActions>
                        <ActionsMenuWrapper>
                            <ActionsMenuToggle
                                onClick={() => setIsActionsMenuOpen(!isActionsMenuOpen)}
                                aria-label="Ações"
                            >
                                Novo Evento
                            </ActionsMenuToggle>
                            {isActionsMenuOpen && (
                                <ActionsMenuList>
                                    <ActionsMenuItem
                                        onClick={() => {
                                            setIsActionsMenuOpen(false);
                                            handleOpenCreateModal();
                                        }}
                                    >
                                        Novo Agendamento
                                    </ActionsMenuItem>
                                    <ActionsMenuItem
                                        onClick={() => {
                                            setIsActionsMenuOpen(false);
                                            handleOpenCreateBlock();
                                        }}
                                    >
                                        Novo Bloqueio
                                    </ActionsMenuItem>
                                </ActionsMenuList>
                            )}
                        </ActionsMenuWrapper>
                    </HeaderActions>
                </div>
                <ControlsContainer>
                    <DateNavigationRow>
                        <DateNavigation>
                            <NavButton onClick={handlePrevDay} aria-label="Dia anterior" data-testid="prev-day-btn">
                                ‹
                            </NavButton>
                            <DateDisplayContainer>
                                <DateDisplay>{formatDate(selectedDate)}</DateDisplay>
                                <input
                                    type="date"
                                    data-testid="date-picker"
                                    style={{
                                        position: 'absolute',
                                        opacity: 0,
                                        inset: 0,
                                        cursor: 'pointer',
                                    }}
                                    value={format(selectedDate, 'yyyy-MM-dd')}
                                    onChange={handleDateChange}
                                    onClick={(e) => {
                                        if ('showPicker' in HTMLInputElement.prototype) {
                                            try {
                                                (e.target as HTMLInputElement).showPicker();
                                            } catch {
                                                // showPicker not supported
                                            }
                                        }
                                    }}
                                />
                            </DateDisplayContainer>
                            <TodayButton
                                className={!isToday(selectedDate) ? 'visible' : ''}
                                onClick={handleToday}
                                aria-label="Voltar para Hoje"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    style={{ width: 18, height: 18 }}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </TodayButton>
                            <NavButton onClick={handleNextDay} aria-label="Próximo dia" data-testid="next-day-btn">
                                ›
                            </NavButton>
                        </DateNavigation>
                        {showRefreshButton && <RefreshButton onRefresh={handleRefresh} disabled={isLoadingEvents} />}
                    </DateNavigationRow>
                    {isOwner && (
                        <FilterGroup>
                            <FilterLabel>Profissional</FilterLabel>
                            <Select
                                data-testid="worker-filter"
                                value={workerFilter === 'all' ? '' : String(workerFilter)}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const newFilter = val === '' ? 'all' : Number(val);
                                    gtagEvent('bookings_filter_worker_click', { item_id: newFilter });
                                    setWorkerFilter(newFilter);
                                }}
                            >
                                <option value="">Todos</option>
                                {workers.map((worker) => (
                                    <option key={worker.id} value={worker.id}>
                                        {worker.name}
                                    </option>
                                ))}
                            </Select>
                        </FilterGroup>
                    )}
                </ControlsContainer>
            </Header>

            {businessLoadError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{businessLoadError}</div>
                    <Button onClick={onReloadBusiness} disabled={isLoadingBusiness}>
                        {isLoadingBusiness ? 'Carregando...' : 'Tentar novamente'}
                    </Button>
                </div>
            ) : (
                <AgendaContainer data-testid="bookings-timeline">
                    {isLoading && !loadError ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            Carregando agendamentos...
                        </div>
                    ) : loadError ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{loadError}</div>
                            <Button onClick={handleRetryLoadEvents}>Tentar novamente</Button>
                        </div>
                    ) : agendaItems.length > 0 ? (
                        agendaItems.map((item) =>
                            item.type === 'hours-marker' ? (
                                <HoursMarker key={`marker-${item.time}`}>{item.label}</HoursMarker>
                            ) : item.type === 'empty-interval' ? (
                                <div
                                    key={`empty-${item.time}`}
                                    style={{ textAlign: 'center', padding: '40px', color: '#888' }}
                                >
                                    {item.usesDayLabel
                                        ? 'Nenhum agendamento para este dia.'
                                        : 'Nenhum agendamento para este intervalo.'}
                                </div>
                            ) : (
                                <AgendaItem key={item.id}>
                                    <EventCard
                                        data-testid={`event-card-${item.id}`}
                                        type={item.type}
                                        onClick={() => handleSelectEvent(item)}
                                        style={
                                            item.type === 'booking' && item.payment && !item.payment.paidAt
                                                ? { backgroundColor: vars.warningTintSoft }
                                                : undefined
                                        }
                                    >
                                        <EventTitle
                                            style={
                                                item.recurrenceIntervalDays !== null
                                                    ? { display: 'flex', alignItems: 'center', gap: 6 }
                                                    : undefined
                                            }
                                        >
                                            <span
                                                style={
                                                    item.recurrenceIntervalDays !== null
                                                        ? {
                                                              overflow: 'hidden',
                                                              textOverflow: 'ellipsis',
                                                              whiteSpace: 'nowrap',
                                                              minWidth: 0,
                                                          }
                                                        : undefined
                                                }
                                            >
                                                {getServiceName(item) || item.title}
                                            </span>
                                            {item.recurrenceIntervalDays !== null && (
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    style={{ width: 14, height: 14, flexShrink: 0 }}
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                    />
                                                </svg>
                                            )}
                                        </EventTitle>
                                        <EventMeta>
                                            <MetaItem>
                                                <MetaLabel>Horário</MetaLabel>
                                                <MetaValue>
                                                    <TimeBadge>
                                                        {format(item.start, 'HH:mm')} - {format(item.end, 'HH:mm')}
                                                    </TimeBadge>
                                                </MetaValue>
                                            </MetaItem>
                                            {item.type === 'booking' && (
                                                <>
                                                    {item.worker?.name && (
                                                        <MetaItem>
                                                            <MetaLabel>Profissional</MetaLabel>
                                                            <MetaValue>{item.worker.name}</MetaValue>
                                                        </MetaItem>
                                                    )}
                                                    <MetaItem>
                                                        <MetaLabel>Cliente</MetaLabel>
                                                        <MetaValue>{item.customerName}</MetaValue>
                                                    </MetaItem>
                                                    <MetaItem>
                                                        <MetaLabel>
                                                            {
                                                                getClientContactInfo({
                                                                    phoneNumber: item.customerPhoneNumber,
                                                                    email: item.customerEmail,
                                                                }).label
                                                            }
                                                        </MetaLabel>
                                                        <MetaValue>
                                                            {
                                                                getClientContactInfo({
                                                                    phoneNumber: item.customerPhoneNumber,
                                                                    email: item.customerEmail,
                                                                }).value
                                                            }
                                                        </MetaValue>
                                                    </MetaItem>
                                                </>
                                            )}
                                            {item.type === 'blocked' && (
                                                <>
                                                    <MetaItem>
                                                        <MetaLabel>Escopo</MetaLabel>
                                                        <MetaValue>
                                                            {item.worker?.name ? 'Profissional' : 'Estabelecimento'}
                                                        </MetaValue>
                                                    </MetaItem>
                                                    {item.worker?.name && (
                                                        <MetaItem>
                                                            <MetaLabel>Profissional</MetaLabel>
                                                            <MetaValue>{item.worker.name}</MetaValue>
                                                        </MetaItem>
                                                    )}
                                                </>
                                            )}
                                        </EventMeta>
                                        {item.type === 'booking' &&
                                            (item.cancelledAt ||
                                                (confirmationEnabled && item.confirmedAt) ||
                                                (checkInEnabled && item.checkedInAt) ||
                                                item.payment?.paidAt ||
                                                item.isLinkedToSubscription ||
                                                item.intakeStatus === 'completed') && (
                                                <div
                                                    style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}
                                                >
                                                    {item.type === 'booking' && (
                                                        <>
                                                            {item.cancelledAt && (
                                                                <Badge tone="danger" data-testid="badge-cancelled">
                                                                    Cancelado
                                                                </Badge>
                                                            )}
                                                            {confirmationEnabled && item.confirmedAt && (
                                                                <Badge tone="success">Confirmado</Badge>
                                                            )}
                                                            {checkInEnabled && item.checkedInAt && (
                                                                <Badge tone="info" data-testid="badge-checkin">
                                                                    Check-in
                                                                </Badge>
                                                            )}
                                                            {item.payment?.paidAt && <Badge tone="gold">Pago</Badge>}
                                                            {item.isLinkedToSubscription && <Badge>Assinatura</Badge>}
                                                            {item.intakeStatus === 'completed' && (
                                                                <Badge tone="violet">Formulário</Badge>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                    </EventCard>
                                </AgendaItem>
                            ),
                        )
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            {agendaWorker === null
                                ? 'Estabelecimento fechado neste dia.'
                                : `${firstName(agendaWorker.name)} não atende neste dia.`}
                        </div>
                    )}
                    {!isLoading && !loadError && cancelledCount > 0 && !showCancelled && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                backgroundColor: vars.backgroundColorDark,
                                border: `1px solid ${vars.dividerColor}`,
                                borderRadius: 8,
                                fontSize: '0.9rem',
                                color: vars.foregroundColor,
                            }}
                        >
                            <span>
                                {cancelledCount === 1
                                    ? 'Há 1 agendamento que não foi exibido porque foi cancelado.'
                                    : `Há ${cancelledCount} agendamentos que não foram exibidos porque foram cancelados.`}
                            </span>
                            <Button
                                onClick={() => {
                                    gtagEvent('bookings_show_cancelled_click');
                                    setShowCancelled(true);
                                }}
                                data-testid="show-cancelled-btn"
                            >
                                Mostrar
                            </Button>
                        </div>
                    )}
                </AgendaContainer>
            )}

            <ModalWrapper hideOnDesktop={confirmDialog.isOpen}>
                <Modal
                    isOpen={!!selectedEvent}
                    onClose={handleCloseModal}
                    title={selectedEvent?.type === 'blocked' ? 'Detalhes do Bloqueio' : 'Detalhes do Agendamento'}
                    data-testid={selectedEvent?.type === 'blocked' ? 'block-detail-modal' : 'booking-detail-modal'}
                    footer={renderModalFooter()}
                >
                    {renderModalContent()}
                </Modal>
            </ModalWrapper>

            <Modal
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
                title="Novo Agendamento"
                data-testid="booking-modal"
                footer={
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            alignItems: 'flex-end',
                            gap: '12px',
                        }}
                    >
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button data-testid="modal-cancel" onClick={handleCloseCreateModal} disabled={isCreating}>
                                Cancelar
                            </Button>
                            <Button
                                data-testid="modal-save"
                                primary
                                type="submit"
                                form="booking-form"
                                disabled={isCreating}
                            >
                                {isCreating ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </div>
                    </div>
                }
            >
                <form
                    id="booking-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateBooking();
                    }}
                >
                    <FormGroup>
                        {isOwner && (
                            <InputGroup data-testid="worker-picker">
                                Profissional
                                <Select
                                    value={draftBooking.workerId ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const newWorkerId = value === '' ? null : Number(value);
                                        // Clear selected services when changing worker
                                        setDraftBooking((prev) => ({
                                            ...prev,
                                            workerId: newWorkerId,
                                            services: [],
                                        }));
                                    }}
                                >
                                    <option value="">Selecione um profissional</option>
                                    {workers
                                        .filter((w) => w.isActive)
                                        .map((worker) => (
                                            <option key={worker.id} value={worker.id}>
                                                {worker.name}
                                            </option>
                                        ))}
                                </Select>
                            </InputGroup>
                        )}
                        {draftBooking.workerId && (
                            <InputGroup>
                                Serviços
                                {(() => {
                                    const selectedWorker = workers.find((w) => w.id === draftBooking.workerId);
                                    const availableServicesForWorker = availableServices.filter((service) =>
                                        selectedWorker?.servicesIds.includes(service.id),
                                    );

                                    if (availableServicesForWorker.length === 0) {
                                        return (
                                            <div style={{ fontSize: '0.85rem', color: '#666', fontWeight: 400 }}>
                                                Este profissional não possui serviços cadastrados
                                            </div>
                                        );
                                    }

                                    return (
                                        <CheckboxGroup style={{ padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
                                            {availableServicesForWorker.map((service) => (
                                                <CheckboxLabel key={service.id}>
                                                    <Checkbox
                                                        checked={draftBooking.services.includes(service.id)}
                                                        onChange={() => handleToggleService(service.id)}
                                                    />
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span>{service.name}</span>
                                                        <small style={{ color: '#666' }}>
                                                            {service.durationInMinutes} min
                                                        </small>
                                                    </div>
                                                </CheckboxLabel>
                                            ))}
                                        </CheckboxGroup>
                                    );
                                })()}
                            </InputGroup>
                        )}
                        <InputGroup>
                            Cliente
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isLoadingClients ? (
                                    <Input value="Carregando clientes..." disabled style={{ flex: 1 }} />
                                ) : clientsLoadError ? (
                                    <>
                                        <Input value={clientsLoadError} disabled style={{ flex: 1, color: '#888' }} />
                                        <button
                                            type="button"
                                            onClick={onReloadClients}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '36px',
                                                height: '36px',
                                                padding: 0,
                                                border: `1px solid ${vars.dividerColor}`,
                                                borderRadius: '6px',
                                                background: vars.backgroundColor,
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                            }}
                                            title="Tentar novamente"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                                style={{ width: 18, height: 18 }}
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                />
                                            </svg>
                                        </button>
                                    </>
                                ) : (
                                    <Select
                                        value={draftBooking.customerId ?? ''}
                                        onChange={(e) => {
                                            const clientId = e.target.value ? Number(e.target.value) : null;
                                            const client = clients.find((c) => c.id === clientId);
                                            setDraftBooking((prev) => ({
                                                ...prev,
                                                customerId: clientId,
                                                customerName: client?.name ?? prev.customerName,
                                                isLinkedToSubscription: false,
                                            }));
                                        }}
                                        style={{ flex: 1 }}
                                    >
                                        <option value="">Selecione um cliente</option>
                                        {clients
                                            .filter((c) => c.name)
                                            .map((client) => (
                                                <option key={client.id} value={client.id}>
                                                    {client.name}
                                                </option>
                                            ))}
                                    </Select>
                                )}
                            </div>
                        </InputGroup>
                        {selectedClient &&
                            (() => {
                                const contactInfo = getClientContactInfo(selectedClient);
                                return (
                                    <InputGroup>
                                        {contactInfo.label}
                                        <Input
                                            type={contactInfo.label === 'E-mail' ? 'email' : 'tel'}
                                            value={contactInfo.value}
                                            readOnly
                                            disabled
                                        />
                                    </InputGroup>
                                );
                            })()}
                        <InputGroup>
                            Data
                            <Input
                                type="date"
                                value={draftBooking.date}
                                onChange={(e) => handleDraftChange('date', e.target.value)}
                            />
                        </InputGroup>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <InputGroup>
                                Início
                                <Input
                                    key={bookingTimeKey}
                                    type="time"
                                    step={300}
                                    defaultValue={draftBooking.startTime}
                                    onChange={(e) => handleDraftChange('startTime', e.target.value)}
                                    onBlur={(e) => {
                                        const rounded = roundTimeToInterval(e.target.value, 5);
                                        if (rounded !== e.target.value) {
                                            handleDraftChange('startTime', rounded);
                                            setBookingTimeKey((k) => k + 1);
                                        }
                                    }}
                                />
                            </InputGroup>
                            <InputGroup>
                                Término
                                <Input type="text" value={computedEndTime ?? ''} readOnly placeholder="--:--" />
                            </InputGroup>
                        </div>
                        <hr style={{ border: 'none', borderTop: `1px solid ${vars.dividerColor}`, margin: '4px 0' }} />
                        <InputGroup style={{ gap: '10px', alignItems: 'flex-start' }}>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={draftBooking.recurrenceEnabled}
                                    onChange={(e) => {
                                        const enabled = e.target.checked;
                                        setDraftBooking((prev) => ({
                                            ...prev,
                                            recurrenceEnabled: enabled,
                                            recurrenceIntervalDays: enabled
                                                ? (prev.recurrenceIntervalDays ?? null)
                                                : null,
                                            recurrenceEndDate: enabled ? prev.recurrenceEndDate : '',
                                        }));
                                    }}
                                />
                                Repetir
                            </CheckboxLabel>
                        </InputGroup>
                        {draftBooking.recurrenceEnabled && (
                            <>
                                <InputGroup>
                                    Repetir a cada
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Input
                                            type="number"
                                            value={draftBooking.recurrenceIntervalDays ?? ''}
                                            onChange={(e) =>
                                                handleDraftChange(
                                                    'recurrenceIntervalDays',
                                                    e.target.value === '' ? null : Number(e.target.value),
                                                )
                                            }
                                            placeholder="Ex.: 7"
                                            min={1}
                                            step={1}
                                        />
                                        <span>dias</span>
                                    </div>
                                </InputGroup>
                                <InputGroup style={{ gap: '10px', alignItems: 'flex-start' }}>
                                    <CheckboxLabel>
                                        <Checkbox
                                            checked={draftBooking.recurrenceEndDate !== ''}
                                            onChange={(e) => {
                                                const minDate = format(
                                                    addDays(parseISO(draftBooking.date), 1),
                                                    'yyyy-MM-dd',
                                                );
                                                handleDraftChange('recurrenceEndDate', e.target.checked ? minDate : '');
                                            }}
                                            disabled={draftBooking.recurrenceIntervalDays == null}
                                        />
                                        Definir data final
                                    </CheckboxLabel>
                                </InputGroup>
                                {draftBooking.recurrenceEndDate !== '' && (
                                    <InputGroup>
                                        Até
                                        <Input
                                            type="date"
                                            value={draftBooking.recurrenceEndDate}
                                            onChange={(e) => handleDraftChange('recurrenceEndDate', e.target.value)}
                                            min={format(addDays(parseISO(draftBooking.date), 1), 'yyyy-MM-dd')}
                                        />
                                    </InputGroup>
                                )}
                            </>
                        )}
                        {(() => {
                            const activeSubscription = selectedClient?.activeSubscription ?? null;
                            return (
                                activeSubscription && (
                                    <InputGroup style={{ gap: '10px', alignItems: 'flex-start' }}>
                                        <CheckboxLabel>
                                            <Checkbox
                                                checked={draftBooking.isLinkedToSubscription}
                                                onChange={(e) => {
                                                    setDraftBooking((prev) => ({
                                                        ...prev,
                                                        isLinkedToSubscription: e.target.checked,
                                                    }));
                                                }}
                                            />
                                            Vincular à assinatura ({formatCurrency(activeSubscription.priceInCents)}
                                            /mês)
                                        </CheckboxLabel>
                                    </InputGroup>
                                )
                            );
                        })()}
                        <hr style={{ border: 'none', borderTop: `1px solid ${vars.dividerColor}`, margin: '4px 0' }} />
                        <InputGroup>
                            Observações
                            <TextArea
                                value={draftBooking.notes}
                                onChange={(e) => handleDraftChange('notes', e.target.value)}
                                placeholder="Notas do agendamento"
                                maxLength={300}
                                style={{ minHeight: '80px' }}
                            />
                        </InputGroup>
                    </FormGroup>
                </form>
            </Modal>

            <Modal
                isOpen={isCreateBlockOpen}
                onClose={handleCloseCreateBlock}
                title="Novo Bloqueio"
                data-testid="block-modal"
                footer={
                    <>
                        <Button data-testid="modal-cancel" onClick={handleCloseCreateBlock} disabled={isCreating}>
                            Cancelar
                        </Button>
                        <Button data-testid="modal-save" primary type="submit" form="block-form" disabled={isCreating}>
                            {isCreating ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <form
                    id="block-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleCreateBlock();
                    }}
                >
                    <FormGroup>
                        <InputGroup>
                            Título
                            <Input
                                type="text"
                                value={draftBlock.title}
                                onChange={(e) => handleDraftBlockChange('title', e.target.value)}
                                placeholder="Ex.: Horário bloqueado"
                                maxLength={80}
                            />
                        </InputGroup>
                        {isOwner && (
                            <InputGroup data-testid="scope-picker">
                                Escopo
                                <Select
                                    value={draftBlock.scope}
                                    onChange={(e) => {
                                        const scope = e.target.value as DraftBlock['scope'];
                                        setDraftBlock((prev) => ({
                                            ...prev,
                                            scope,
                                            workerId: scope === 'worker' ? prev.workerId : null,
                                        }));
                                    }}
                                >
                                    <option value="business">Estabelecimento</option>
                                    <option value="worker">Profissional específico</option>
                                </Select>
                            </InputGroup>
                        )}
                        {isOwner && draftBlock.scope === 'worker' && (
                            <InputGroup>
                                Profissional
                                <Select
                                    value={draftBlock.workerId ?? ''}
                                    onChange={(e) =>
                                        setDraftBlock((prev) => ({
                                            ...prev,
                                            workerId: e.target.value === '' ? null : Number(e.target.value),
                                        }))
                                    }
                                >
                                    <option value="">Selecione um profissional</option>
                                    {workers
                                        .filter((w) => w.isActive)
                                        .map((worker) => (
                                            <option key={worker.id} value={worker.id}>
                                                {worker.name}
                                            </option>
                                        ))}
                                </Select>
                            </InputGroup>
                        )}
                        <InputGroup>
                            Data
                            <Input
                                type="date"
                                value={draftBlock.date}
                                onChange={(e) => handleDraftBlockChange('date', e.target.value)}
                            />
                        </InputGroup>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <InputGroup>
                                Início
                                <Input
                                    key={`block-start-${blockTimeKey}`}
                                    type="time"
                                    step={300}
                                    defaultValue={draftBlock.startTime}
                                    onChange={(e) => handleDraftBlockChange('startTime', e.target.value)}
                                    onBlur={(e) => {
                                        const rounded = roundTimeToInterval(e.target.value, 5);
                                        if (rounded !== e.target.value) {
                                            handleDraftBlockChange('startTime', rounded);
                                            setBlockTimeKey((k) => k + 1);
                                        }
                                    }}
                                />
                            </InputGroup>
                            <InputGroup>
                                Término
                                <Input
                                    key={`block-end-${blockTimeKey}`}
                                    type="time"
                                    step={300}
                                    defaultValue={displayEndTime(draftBlock.endTime)}
                                    onChange={(e) =>
                                        handleDraftBlockChange('endTime', canonicalizeEndTime(e.target.value))
                                    }
                                    onBlur={(e) => {
                                        const rounded = roundTimeToInterval(e.target.value, 5);
                                        if (rounded !== e.target.value) {
                                            handleDraftBlockChange('endTime', canonicalizeEndTime(rounded));
                                            setBlockTimeKey((k) => k + 1);
                                        }
                                    }}
                                />
                            </InputGroup>
                        </div>
                        <hr style={{ border: 'none', borderTop: `1px solid ${vars.dividerColor}`, margin: '4px 0' }} />
                        <InputGroup style={{ gap: '10px', alignItems: 'flex-start' }}>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={draftBlock.recurrenceEnabled}
                                    onChange={(e) => {
                                        const enabled = e.target.checked;
                                        setDraftBlock((prev) => ({
                                            ...prev,
                                            recurrenceEnabled: enabled,
                                            recurrenceIntervalDays: enabled
                                                ? (prev.recurrenceIntervalDays ?? null)
                                                : null,
                                            recurrenceEndDate: enabled ? prev.recurrenceEndDate : '',
                                        }));
                                    }}
                                />
                                Repetir
                            </CheckboxLabel>
                        </InputGroup>
                        {draftBlock.recurrenceEnabled && (
                            <>
                                <InputGroup>
                                    Repetir a cada
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Input
                                            type="number"
                                            value={draftBlock.recurrenceIntervalDays ?? ''}
                                            onChange={(e) =>
                                                handleDraftBlockChange(
                                                    'recurrenceIntervalDays',
                                                    e.target.value === '' ? null : Number(e.target.value),
                                                )
                                            }
                                            placeholder="Ex.: 7"
                                            min={1}
                                            step={1}
                                        />
                                        <span>dias</span>
                                    </div>
                                </InputGroup>
                                <InputGroup style={{ gap: '10px', alignItems: 'flex-start' }}>
                                    <CheckboxLabel>
                                        <Checkbox
                                            checked={draftBlock.recurrenceEndDate !== ''}
                                            onChange={(e) => {
                                                const minDate = format(
                                                    addDays(parseISO(draftBlock.date), 1),
                                                    'yyyy-MM-dd',
                                                );
                                                handleDraftBlockChange(
                                                    'recurrenceEndDate',
                                                    e.target.checked ? minDate : '',
                                                );
                                            }}
                                            disabled={draftBlock.recurrenceIntervalDays == null}
                                        />
                                        Definir data final
                                    </CheckboxLabel>
                                </InputGroup>
                                {draftBlock.recurrenceEndDate !== '' && (
                                    <InputGroup>
                                        Até
                                        <Input
                                            type="date"
                                            value={draftBlock.recurrenceEndDate}
                                            onChange={(e) =>
                                                handleDraftBlockChange('recurrenceEndDate', e.target.value)
                                            }
                                            min={format(addDays(parseISO(draftBlock.date), 1), 'yyyy-MM-dd')}
                                        />
                                    </InputGroup>
                                )}
                            </>
                        )}
                    </FormGroup>
                </form>
            </Modal>

            {confirmDialog.isRecurring ? (
                <Dialog
                    isOpen={confirmDialog.isOpen}
                    onClose={() => !isCreating && closeConfirmDialog()}
                    allowCloseOnOverlay={!isCreating}
                    data-testid="cancel-scope-dialog"
                >
                    <DialogHeader>
                        <DialogTitle>{confirmDialog.title}</DialogTitle>
                    </DialogHeader>
                    <DialogContent style={{ opacity: 0.8 }}>{confirmDialog.message}</DialogContent>
                    <DialogFooter style={{ flexDirection: 'column' }}>
                        <Button onClick={() => !isCreating && closeConfirmDialog()} disabled={isCreating}>
                            Voltar
                        </Button>
                        <Button
                            danger
                            onClick={() => {
                                setConfirmDialog((prev) => ({ ...prev, scope: 'all' }));
                                confirmDialog.onConfirm('all');
                            }}
                            disabled={isCreating}
                            data-testid="cancel-scope-all"
                        >
                            {isCreating && confirmDialog.scope === 'all' ? 'Removendo...' : 'Este e os seguintes'}
                        </Button>
                        <Button
                            danger
                            primary
                            onClick={() => {
                                setConfirmDialog((prev) => ({ ...prev, scope: 'single' }));
                                confirmDialog.onConfirm('single');
                            }}
                            disabled={isCreating}
                            data-testid="cancel-scope-single"
                        >
                            {isCreating && confirmDialog.scope === 'single' ? 'Removendo...' : 'Apenas este'}
                        </Button>
                    </DialogFooter>
                </Dialog>
            ) : (
                <ConfirmDialog
                    isOpen={confirmDialog.isOpen}
                    title={confirmDialog.title}
                    message={confirmDialog.message}
                    confirmText={confirmDialog.confirmText}
                    onConfirm={() => confirmDialog.onConfirm()}
                    onCancel={closeConfirmDialog}
                    isLoading={isCreating}
                />
            )}
            <ConfirmDialog
                isOpen={blockOverlapDialog.isOpen}
                title="Há agendamentos nesse horário"
                message={renderBlockOverlapMessage({
                    bookings: blockOverlapDialog.bookings,
                    rangeCappedAt: blockOverlapDialog.rangeCappedAt,
                    services: availableServices,
                    workers: draftBlock.scope === 'business' ? workers : null,
                })}
                confirmText="Cancelar e bloquear"
                cancelText="Voltar"
                onConfirm={handleConfirmBlockOverlap}
                onCancel={() => setBlockOverlapDialog((prev) => ({ ...prev, isOpen: false }))}
            />
            <Dialog
                isOpen={failedCancellationsDialog.isOpen}
                onClose={() => setFailedCancellationsDialog((prev) => ({ ...prev, isOpen: false }))}
                variant="warning"
                data-testid="failed-cancellations-dialog"
            >
                <DialogHeader>
                    <DialogTitle>Alguns agendamentos não foram cancelados</DialogTitle>
                </DialogHeader>
                <DialogContent>
                    {renderFailedCancellationsMessage({
                        bookings: failedCancellationsDialog.bookings,
                        services: availableServices,
                        workers: failedCancellationsDialog.isBusinessScope ? workers : null,
                    })}
                </DialogContent>
                <DialogFooter>
                    <Button
                        onClick={() => setFailedCancellationsDialog((prev) => ({ ...prev, isOpen: false }))}
                        data-testid="failed-cancellations-close"
                    >
                        Fechar
                    </Button>
                </DialogFooter>
            </Dialog>
        </Container>
    );
}

function parseDate(iso: string): { year: number; month: number; day: number } {
    const [year, month, day] = iso.split('-').map(Number);
    return { year: year!, month: month!, day: day! };
}

function parseTime(hhmm: string): { hour: number; minute: number } {
    const [hour, minute] = hhmm.split(':').map(Number);
    return { hour: hour!, minute: minute! };
}

function buildBlockParams(draft: DraftBlock): {
    title: string;
    date: { year: number; month: number; day: number };
    start: { hour: number; minute: number };
    end: { hour: number; minute: number };
    workerId: number | null;
    recurrence: { intervalDays: number; endDate: { year: number; month: number; day: number } | null } | null;
} {
    return {
        title: draft.title,
        date: parseDate(draft.date),
        start: parseTime(draft.startTime),
        end: parseTime(draft.endTime),
        workerId: draft.scope === 'worker' ? draft.workerId : null,
        recurrence:
            draft.recurrenceEnabled && draft.recurrenceIntervalDays !== null
                ? {
                      intervalDays: draft.recurrenceIntervalDays,
                      endDate: draft.recurrenceEndDate ? parseDate(draft.recurrenceEndDate) : null,
                  }
                : null,
    };
}

function formatOverlapDate(date: { year: number; month: number; day: number }): string {
    return `${String(date.day).padStart(2, '0')}/${String(date.month).padStart(2, '0')}/${date.year}`;
}

function formatOverlapTime(time: { hour: number; minute: number }): string {
    return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
}

function renderBlockOverlapMessage(params: {
    bookings: OverlappingBooking[];
    rangeCappedAt: { year: number; month: number; day: number } | null;
    services: BusinessServiceItem[];
    workers: Worker[] | null;
}): React.ReactNode {
    const { bookings, rangeCappedAt, services, workers } = params;
    const renderItem = (b: OverlappingBooking) => renderBookingLine(b, { services, workers });

    const cappedNote = rangeCappedAt ? (
        <p>
            <em>
                Mostrando conflitos dos próximos 90 dias (até {formatOverlapDate(rangeCappedAt)}). Agendamentos futuros
                podem ser afetados.
            </em>
        </p>
    ) : null;

    if (bookings.length <= 5) {
        return (
            <div>
                <p>Os seguintes agendamentos serão cancelados se você continuar:</p>
                <div>{bookings.map(renderItem)}</div>
                {cappedNote}
            </div>
        );
    }

    const sortedDates = bookings.map((b) => b.date).sort((a, b) => new Rich.SimpleDate(a).compareTo(b));
    return (
        <div>
            <p>
                {bookings.length} agendamentos entre {formatOverlapDate(sortedDates[0]!)} e{' '}
                {formatOverlapDate(sortedDates[sortedDates.length - 1]!)} serão cancelados.
            </p>
            <p>Exemplos:</p>
            <div>{bookings.slice(0, 3).map(renderItem)}</div>
            {cappedNote}
        </div>
    );
}

function renderFailedCancellationsMessage(params: {
    bookings: OverlappingBooking[];
    services: BusinessServiceItem[];
    workers: Worker[] | null;
}): React.ReactNode {
    const { bookings, services, workers } = params;
    return (
        <div>
            <p style={{ marginTop: 0 }}>
                O bloqueio foi criado, mas não foi possível cancelar{' '}
                {bookings.length === 1 ? 'o agendamento abaixo' : `os ${bookings.length} agendamentos abaixo`}. Cancele
                manualmente pela agenda:
            </p>
            <div>{bookings.map((b) => renderBookingLine(b, { services, workers }))}</div>
        </div>
    );
}

function renderBookingLine(
    b: OverlappingBooking,
    params: { services: BusinessServiceItem[]; workers: Worker[] | null },
): React.ReactNode {
    const { services, workers } = params;
    const serviceName = (id: number) => services.find((s) => s.id === id)?.name ?? `#${id}`;
    const workerName = (id: number) => workers?.find((w) => w.id === id)?.name ?? `#${id}`;
    const svcs = b.servicesIds.map(serviceName).join(', ') || '—';
    const workerSuffix = workers ? ` — ${workerName(b.workerId)}` : '';
    const recurrenceSuffix = b.isRecurring
        ? ` (recorrente, ${b.totalAffectedOccurrences} ocorrência${b.totalAffectedOccurrences > 1 ? 's' : ''} afetada${b.totalAffectedOccurrences > 1 ? 's' : ''})`
        : '';
    return (
        <div key={b.bookingId} style={{ marginTop: 4 }}>
            <strong>{b.clientName ?? 'Cliente'}</strong> — {svcs} — {formatOverlapDate(b.date)}{' '}
            {formatOverlapTime(b.time.start)}
            {workerSuffix}
            {recurrenceSuffix}
        </div>
    );
}
